/**
 * WebSocket service for real-time communication
 * Handles WebSocket connection, messages, and event handlers
 */

import { Dispatch } from '@reduxjs/toolkit';
import { USE_CASE_INCIDENT_REPORT, DEFAULT_LANGUAGE_CODE, FIELD_VALUE_EXTRACTING } from '../constants';
import {
  wsConnecting,
  wsConnected,
  wsDisconnected,
  wsError,
  transcriptionStarted,
  transcriptionPaused,
  transcriptionStopped,
  enableNoteRecording,
  setActiveTranscriptId,
} from '../store/recordingSlice';
import {
  addTranscriptSegment,
  updateSegmentTranslation,
  TranscriptSegment,
} from '../store/transcriptSlice';
import {
  updateFields,
  markFieldAsExtracted,
  setAnalyzing,
  setAnalysisError,
} from '../store/analysisSlice';
import { fetchSession, updateCurrentSessionFields } from '../store/sessionSlice';
import { fetchTranscript } from '../store/transcriptSlice';
import { isFieldPending } from '../utils/fieldUtils';
import { TOKEN_STORAGE_KEY } from '../types/constants';
import store from '../store/store';
import authService from './authService';

// ===== TYPES =====

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export interface TranscribeResultMessage {
  type: 'transcribe_result';
  text: string;
  is_final: boolean;
  speaker: string | null;
  timestamp?: number;
}

// ===== SERVICE =====

class WebSocketService {
  private ws: WebSocket | null = null;
  private dispatch: Dispatch | null = null;
  private connectionId: string | null = null;  // Client-generated connection ID
  private sessionId: string | null = null;      // Real session ID from backend
  private transcriptId: string | null = null;   // Real transcript ID from backend (set on session_created)
  private isStopping = false;                   // True when we initiated stop, prevents stale 'paused' from killing new recordings
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  /**
   * Initialize the WebSocket service with Redux dispatch
   */
  initialize(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  /**
   * Close WebSocket connection without clearing session state
   * Used for cleanup while preserving session continuity
   */
  private closeWebSocket() {
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      console.log('Closing WebSocket connection');
      this.ws.close(1000, 'Closing connection');
      this.ws = null;
    }
  }

  /**
   * Connect to WebSocket server
   * Refreshes the auth token before connecting to prevent "Token has expired" errors.
   */
  async connect(connectionId: string, existingSessionId?: string): Promise<void> {
    // Close existing WebSocket connection before creating a new one
    // This prevents accumulation of multiple connections that all receive the same messages
    this.closeWebSocket();
    
    this.connectionId = connectionId;
    this.sessionId = existingSessionId || null;  // Store existing session ID if resuming
    this.isStopping = false;  // Reset on new connection — any stale 'paused' from old connection should be ignored
    
    // Ensure localStorage has a fresh token before connecting.
    // refreshToken() calls Cognito SDK which updates localStorage automatically.
    // Combined with the periodic refresh in App.tsx, this prevents "Token has expired" errors.
    await authService.refreshToken();
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    
    if (!token) {
      console.warn('No authentication token available for WebSocket connection');
    }

    return new Promise((resolve, reject) => {
      
      // Get WebSocket URL based on environment
      const apiBaseUrl = "https://beta.na.api.navigator.whs.amazon.dev";
      const stage = "beta".toLowerCase();
      
      // Determine WebSocket base URL (matching the logic in apiClient.ts)
      let wsBaseUrl: string;
      if (window.location.hostname === 'localhost' && 
          (apiBaseUrl.includes('localhost:8080') || apiBaseUrl.includes('https://beta.na.api.navigator.whs.amazon.dev'))) {
        // Use localhost when running locally AND webpack config has localhost:8080
        wsBaseUrl = 'ws://localhost:8080';
      } else {
        // Otherwise use stage-specific WSS URL
        wsBaseUrl = `wss://alb.${stage}.na.navigator-api.whs.amazon.dev`;
      }
      
      // Build WebSocket URL with connection ID and authentication token
      const params = new URLSearchParams();
      if (token) {
        // Add token with Bearer prefix as Authorization query parameter
        params.append('Authorization', `Bearer ${token}`);
      }
      
      const wsUrl = `${wsBaseUrl}/ws/recording/${connectionId}${params.toString() ? '?' + params.toString() : ''}`;

      console.log('Connecting to WebSocket:', wsUrl.replace(/Authorization=[^&]+/, 'Authorization=***'));
      this.dispatch?.(wsConnecting());

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          this.dispatch?.(wsConnected());
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.dispatch?.(wsError('WebSocket connection error'));
          reject(new Error('WebSocket connection error'));
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.dispatch?.(wsDisconnected());
          
          // Don't reconnect if closed due to auth failure (code 1008 = Policy Violation)
          // The backend sends 1008 when token validation fails.
          // Reconnecting with the same stale token would just generate more errors.
          if (event.code === 1008) {
            console.warn('WebSocket closed due to authentication failure. Not reconnecting — token needs refresh.');
            this.reconnectAttempts = this.maxReconnectAttempts; // Prevent further attempts
            return;
          }
          
          // Attempt to reconnect if not a normal closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect();
          }
        };
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        this.dispatch?.(wsError('Failed to create WebSocket connection'));
        reject(error);
      }
    });
  }

  /**
   * Attempt to reconnect after disconnection
   */
  private attemptReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      if (this.connectionId) {
        // Generate new connection ID for reconnect
        const newConnectionId = `conn-${Date.now()}`;
        this.connect(newConnectionId, this.sessionId || undefined).catch(console.error);
      }
    }, delay);
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string) {
    try {
      const message: WebSocketMessage = JSON.parse(data);
      
      switch (message.type) {
        case 'connected':
          console.log('Server confirmed connection:', message.data?.connection_id);
          break;

        case 'session_created':
          this.handleSessionCreated(message);
          break;

        case 'transcript_update':
          // Backend sends transcript_update with segment data
          this.handleTranscriptUpdate(message);
          break;

        case 'transcribe_result':
          // Backward compatibility
          this.handleTranscribeResult(message as TranscribeResultMessage);
          break;

        case 'field_update':
          // Backend sends field_update for LLM analysis
          this.handleFieldUpdate(message);
          break;

        case 'recording_status':
          // Backend sends recording_status for status changes
          this.handleRecordingStatus(message);
          break;

        case 'transcript_translation':
          // Backend sends translation for an existing segment (Phase 2 of bilingual flow)
          this.handleTranscriptTranslation(message);
          break;

        case 'evidence_updated':
          // Evidence extraction completed — re-fetch session and transcript to get highlights
          this.handleEvidenceUpdated(message);
          break;

        case 'note_recording_started':
          // Note recording started confirmation
          console.log('Note recording started');
          break;

        case 'note_transcript_update':
        case 'note_transcript_complete':
          // These are handled by the note transcript callback
          // if registered, otherwise just log
          console.log('Note transcript update:', message.type);
          break;

        case 'error':
          // Error message from backend
          console.error('WebSocket error from backend:', message.data);
          break;

        default:
          console.log('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  /**
   * Handle session_created message from backend
   */
  private handleSessionCreated(message: any) {
    const session = message.data?.session;
    if (session && session.session_id) {
      this.sessionId = session.session_id;
      // Capture real transcript_id so live segments use it instead of 'live'
      // This ensures confirmed segments render in black text immediately
      this.transcriptId = message.data?.transcript_id || null;
      this.dispatch?.(setActiveTranscriptId(this.transcriptId || 'live'));
      console.log('Session created/resumed:', this.sessionId, 'transcriptId:', this.transcriptId);
      
      if (this.dispatch) {
        (this.dispatch as any)(fetchSession(session.session_id));
        (this.dispatch as any)(fetchTranscript(session.session_id));
      }
    }
  }

  /**
   * Handle transcript_update message from backend
   * Uses real transcript_id from session_created when available, falls back to 'live'
   * Supports bilingual fields: detected_language, segment_id
   */
  private handleTranscriptUpdate(message: any) {
    const segmentData = message.data?.segment;
    if (!segmentData) return;

    const segment: TranscriptSegment = {
      id: `${Date.now()}-${Math.random()}`,
      transcript_id: this.transcriptId || 'live',
      text: segmentData.text,
      speaker: segmentData.speaker || null,
      timestamp: Date.now(),
      isPartial: segmentData.is_partial,
      // Bilingual fields (present in auto mode)
      detectedLanguage: segmentData.detected_language || undefined,
      segmentId: segmentData.segment_id || undefined,
    };

    this.dispatch?.(addTranscriptSegment(segment));
  }

  /**
   * Handle transcript_translation message from backend (Phase 2 of bilingual flow).
   * Matches segment by segment_id and attaches translations.
   */
  private handleTranscriptTranslation(message: any) {
    const { segment_id, translations } = message.data || {};
    if (!segment_id || !translations) return;

    this.dispatch?.(updateSegmentTranslation({ segmentId: segment_id, translations }));
  }

  /**
   * Handle transcribe result message (backward compatibility)
   * Uses real transcript_id from session_created when available, falls back to 'live'
   */
  private handleTranscribeResult(message: TranscribeResultMessage) {
    const segment: TranscriptSegment = {
      id: `${Date.now()}-${Math.random()}`,
      transcript_id: this.transcriptId || 'live',
      text: message.text,
      speaker: message.speaker,
      timestamp: message.timestamp || Date.now(),
      isPartial: !message.is_final,
    };

    this.dispatch?.(addTranscriptSegment(segment));
  }

  /**
   * Handle field_update message from backend
   */
  private handleFieldUpdate(message: any) {
    if (message.data?.fields) {
      // Filter out <EXTRACTING> for fields that already have a real value.
      // This prevents the race condition where the orchestrator sends <EXTRACTING>
      // for a field that Sonnet already extracted (due to stale DB read).
      const currentExtracted = store.getState().session.currentSession?.extracted_fields || {};
      const fieldsToApply: Record<string, any> = {};

      Object.entries(message.data.fields).forEach(([fieldName, value]) => {
        if (value === FIELD_VALUE_EXTRACTING) {
          // Only apply <EXTRACTING> if current value is unknown/empty
          const currentValue = currentExtracted[fieldName];
          if (!currentValue || currentValue === '<UNKNOWN>') {
            fieldsToApply[fieldName] = value;
          }
          // Otherwise: skip — don't overwrite real value with <EXTRACTING>
        } else {
          fieldsToApply[fieldName] = value;
          if (value && !isFieldPending(value as string | string[])) {
            this.dispatch?.(markFieldAsExtracted(fieldName));
          }
        }
      });

      if (Object.keys(fieldsToApply).length > 0) {
        this.dispatch?.(updateFields(fieldsToApply));
        this.dispatch?.(updateCurrentSessionFields(fieldsToApply));
      }
      this.dispatch?.(setAnalyzing(false));
    }

    if (message.data?.status === 'error') {
      this.dispatch?.(setAnalysisError('Analysis error occurred'));
    }
  }

  /**
   * Handle recording_status message from backend
   */
  private handleRecordingStatus(message: any) {
    const status = message.data?.status;
    console.log('[DEBUG] Recording status update:', status, 'sessionId:', this.sessionId, 'dispatch:', !!this.dispatch);
    
    // Map backend status values to Redux actions
    switch (status) {
      case 'recording':
        this.dispatch?.(transcriptionStarted());
        break;
      case 'paused':
        // Only process cleanup if WE initiated the stop via stop().
        // A stale 'paused' from a previous connection's slow stop_session_core
        // can arrive on a new connection that's already recording — ignore it.
        if (!this.isStopping) {
          console.log('[DEBUG] Ignoring stale paused status (isStopping=false)');
          break;
        }
        this.isStopping = false;

        this.dispatch?.(transcriptionPaused());
        
        // Auto-fetch updated session data when recording stops
        // Backend sends 'paused' status when stop_session is called
        if (this.sessionId && this.dispatch) {
          const sid = this.sessionId; // capture before nulling
          console.log('[DEBUG] Fetching session + transcript for:', sid);
          Promise.all([
            (this.dispatch as any)(fetchSession(sid)),
            (this.dispatch as any)(fetchTranscript(sid))
          ])
            .then((results: any[]) => {
              console.log('[DEBUG] fetchSession + fetchTranscript completed for:', sid, 'results:', results);
              // Finish cleanup - now safe to start note recording
              this.dispatch?.(enableNoteRecording());
            })
            .catch((error: any) => {
              console.error('[DEBUG] Failed to fetch session/transcript after pause:', error);
              // Finish cleanup even on error to unblock UI
              this.dispatch?.(enableNoteRecording());
            });
        } else {
          console.warn('[DEBUG] No sessionId or dispatch when paused! sessionId:', this.sessionId, 'dispatch:', !!this.dispatch);
          // No session to fetch, finish cleanup immediately
          this.dispatch?.(enableNoteRecording());
        }
        
        // Close WebSocket connection and clear session state
        this.closeWebSocket();
        this.sessionId = null;
        this.connectionId = null;
        this.transcriptId = null;
        break;
      case 'completed':
        this.dispatch?.(transcriptionStopped());
        break;
      default:
        console.log('Unknown recording status:', status);
    }
  }

  /**
   * Handle evidence_updated message from backend
   * Evidence extraction completed — re-fetch session and transcript to get highlights
   */
  private handleEvidenceUpdated(message: any) {
    const sessionId = message.data?.session_id || this.sessionId;
    if (sessionId && this.dispatch) {
      console.log('Evidence updated, re-fetching session and transcript:', sessionId);
      (this.dispatch as any)(fetchSession(sessionId));
      (this.dispatch as any)(fetchTranscript(sessionId));
    }
  }

  /**
   * Send a message to the WebSocket server
   */
  send(message: WebSocketMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
      throw new Error('WebSocket is not connected');
    }
  }

  /**
   * Send start_recording message
   *
   * @param existingSessionId - Optional session ID to resume an existing session
   * @param audioFormat - Audio format from browser (e.g., 'audio/pcm')
   * @param sampleRate - Sample rate in Hz (e.g., 48000, 16000)
   * @param useCaseType - Profile/use case type for this session
   * @param languageCode - Language code string (e.g., "en-US" or "en-US,es-US" for bilingual)
   * @param groupId - Site code to associate with the session
   */
  startRecording(
    existingSessionId?: string,
    audioFormat?: string,
    sampleRate?: number,
    useCaseType?: string,
    languageCode?: string,
    groupId?: string
  ) {
    this.dispatch?.(setActiveTranscriptId(null));
    this.send({
      type: 'start_recording',
      data: {
        session_id: existingSessionId,
        use_case_type: useCaseType || USE_CASE_INCIDENT_REPORT,
        language_code: languageCode || DEFAULT_LANGUAGE_CODE,
        audio_format: audioFormat || 'audio/pcm',
        sample_rate: sampleRate || 16000,
        group_id: groupId,
      },
    });
  }

  /**
   * Get current session ID (real DynamoDB session ID)
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /** The active recording's transcript id (set on session_created). */
  getTranscriptId(): string | null {
    return this.transcriptId;
  }

  /**
   * Get current connection ID
   */
  getConnectionId(): string | null {
    return this.connectionId;
  }

  /**
   * Send pause_recording message
   * Note: This pauses transcription but keeps WebSocket connected
   */
  pause() {
    this.send({ 
      type: 'pause_recording',
      data: {}
    });
  }

  /**
   * Send stop_session message
   * Note: This stops recording but keeps WebSocket connected for session continuity
   * The session can be resumed by sending start_recording with the same session_id
   */
  stop() {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      // WebSocket already closed (e.g., backend sent 'paused' from a prior stop that
      // already triggered cleanup). This is safe — the backend already processed the stop.
      console.log('WebSocket already closed, skipping stop_session send');
      // Ensure note recording is re-enabled since the paused handler won't fire
      this.dispatch?.(enableNoteRecording());
      return;
    }
    this.isStopping = true;
    this.send({ 
      type: 'stop_session',
      data: {}
    });
    // NOTE: We do NOT disconnect the WebSocket here
    // The connection stays alive so the user can resume recording in the same session
  }

  /**
   * Send audio data (supports both Blob and ArrayBuffer for PCM)
   */
  sendAudioData(audioData: Blob | ArrayBuffer) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(audioData);
    }
  }


  /**
   * Fully disconnect WebSocket and clear all state.
   * Used when navigating away from a session (e.g., starting a new session)
   * to prevent stale reconnection attempts or message handlers from firing.
   */
  disconnect() {
    // Cancel any pending reconnection
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent further reconnects

    this.closeWebSocket();
    this.sessionId = null;
    this.connectionId = null;
    this.transcriptId = null;
    this.dispatch?.(setActiveTranscriptId(null));
    this.reconnectAttempts = 0; // Reset for next connection
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Send start_note_recording message
   * 
   * @param audioFormat - Audio format from browser (e.g., 'audio/pcm')
   * @param sampleRate - Sample rate in Hz (e.g., 48000, 16000)
   */
  startNoteRecording(audioFormat?: string, sampleRate?: number) {
    this.send({
      type: 'start_note_recording',
      data: {
        audio_format: audioFormat || 'audio/pcm',
        sample_rate: sampleRate || 16000,
      },
    });
  }

  /**
   * Send stop_note_recording message
   */
  stopNoteRecording() {
    this.send({
      type: 'stop_note_recording',
      data: {},
    });
  }

  /**
   * Set callback for note transcript updates
   * @param callback - Function to call when note transcript updates arrive
   */
  onNoteTranscriptUpdate(callback: (text: string, isPartial: boolean) => void) {
    if (this.ws) {
      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'note_transcript_update') {
            callback(message.data?.text || '', message.data?.is_partial || false);
          } else if (message.type === 'note_transcript_complete') {
            callback(message.data?.text || '', false);
          }
        } catch (error) {
          console.error('Error parsing note transcript message:', error);
        }
        
        // Also handle regular messages
        this.handleMessage(event.data);
      };
    }
  }

  /**
   * Remove note transcript callback and restore original message handler
   */
  removeNoteTranscriptCallback() {
    if (this.ws) {
      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
